import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import {
  type PlacementId,
  PlacementStatus,
  type AfsPlacementId,
  type FinnLegendaryAfsPlacementId,
  type AdStatus
} from '@schibsted-nmp/advertising-shared';

import { debug } from './.debug.js';
import { Api } from './Api/index.js';
import type { Interface } from './Api/Interface.js';
import { staggerAfsRequests } from './utils/staggerAfsRequest.js';

const afsRequest = (
  container: AfsPlacementId | FinnLegendaryAfsPlacementId,
  options: Interface.Parameters.PageLevel
) => {
  if (!options.query) return;

  debug
    .withPlacementLabel(container)
    .message('Requesting ad', options)
    .info((log) => log());

  updatePlacementKeyValueById(container, 'status', 'requested');
  updatePlacementKeyValueById(container, 'creativeType', 'banner');

  if (window.location.hostname.includes('local')) {
    updateMetricByKey(container, PlacementStatus.AdLoaded);
    updatePlacementKeyValueById(container, 'status', 'loaded');
    renderAFSPlaceholder(container);
    return;
  }

  try {
    Api()('ads', options, {
      container,

      adLoadedCallback: (...args) => {
        const [, loadIsSuccessful] = args;

        const placementStatus = loadIsSuccessful
          ? PlacementStatus.AdLoaded
          : PlacementStatus.AdNoFill;

        const adStatus: AdStatus = loadIsSuccessful ? 'loaded' : 'error';

        updateMetricByKey(container, placementStatus);
        updatePlacementKeyValueById(container, 'status', adStatus);

        if (loadIsSuccessful) {
          debug.withPlacementLabel(container).message('Loaded').success();
        } else {
          debug
            .withPlacementLabel(container)
            .message('Unfilled impression', args)
            .warn();
        }
      }
    }).catch((error: unknown) => {
      debug
        .withPlacementLabel(container)
        .message('Failed requesting ad', error)
        .error();
    });

    updateMetricByKey(container, PlacementStatus.AdRequested);
  } catch (error) {
    debug
      .withPlacementLabel(container)
      .message('Failed requesting ad', error)
      .error();
  }
};

export function triggerAfsRequest(
  container: AfsPlacementId | FinnLegendaryAfsPlacementId,
  options: Interface.Parameters.PageLevel
) {
  staggerAfsRequests(() => {
    afsRequest(container, options);
  });
}

function renderAFSPlaceholder(placementId: PlacementId) {
  setTimeout(() => {
    const slotElement = document.getElementById(placementId);
    if (!slotElement) return;

    const placeholder = document.createElement('div');

    Object.assign(placeholder.style, {
      backgroundColor: '#f0f0f0',
      border: '1px solid #ccc',
      borderRadius: '10px',
      width: '100%',
      height: '396px',
      lineHeight: '396px',
      textAlign: 'center'
    } as CSSStyleDeclaration);

    placeholder.textContent = 'Adsense placeholder';
    slotElement.innerHTML = '';
    slotElement.appendChild(placeholder);
  }, 50);
}
